import {
  ArrowLeftIcon,
  AtSymbolIcon,
  PaperAirplaneIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

export type SlackLine = {
  from: 'person' | 'agent';
  text: ReactNode;
  at: number;
  time: string;
  /** For the agent: Slack's "is typing" line runs from here until `at`. */
  typingAt?: number;
};

/**
 * A direct message with the agent's Slack app, in Slack's mobile look:
 * square avatars, names with times, and the APP badge on the agent.
 */
export default function SlackChat({
  brand,
  person,
  lines,
}: {
  brand: DemoBrand;
  person: { name: string; initials: string };
  lines: SlackLine[];
}) {
  const { t } = useChannelsT();

  return (
    <PhoneFrame bar='bg-[#3f0e40] text-white'>
      <div className='flex items-center gap-2.5 bg-[#3f0e40] px-3 pb-3 pt-1 text-white'>
        <ArrowLeftIcon className='h-5 w-5 flex-none rtl:-scale-x-100' />
        <span
          className={clsxm(
            'flex h-7 w-7 flex-none items-center justify-center rounded-md text-[12px] font-bold',
            brand.bg,
          )}
        >
          {brand.initial}
        </span>
        <span className='min-w-0 flex-1 truncate text-[15px] font-bold'>
          {brand.name}
        </span>
        <span className='flex-none rounded bg-white/15 px-1 py-px text-[10px] font-bold uppercase tracking-wide'>
          {t('App')}
        </span>
      </div>

      <div className='flex min-h-[25rem] flex-col justify-end gap-3.5 bg-white px-3.5 pb-3 pt-4 text-[13.5px] leading-[1.45] text-[#1d1c1d] dark:bg-[#1a1d21] dark:text-[#d1d2d3]'>
        {lines.map((line, index) => {
          const fromAgent = line.from === 'agent';
          return (
            <div key={index} className='grid'>
              {fromAgent && line.typingAt !== undefined && (
                <p
                  className={clsxm(
                    styles.typing,
                    'self-end text-[11.5px] italic text-[#616061] [grid-area:1/1] dark:text-[#ababad]',
                  )}
                  style={delay(line.typingAt, {
                    '--len': `${line.at - line.typingAt}ms`,
                  })}
                >
                  {t('{{name}} is typing…', { name: brand.name })}
                </p>
              )}
              <div
                className={clsxm(styles.bubble, 'flex gap-2.5 [grid-area:1/1]')}
                style={delay(line.at)}
              >
                <span
                  className={clsxm(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-lg text-[13px] font-bold text-white',
                    fromAgent ? brand.bg : 'bg-[#7c6f64]',
                  )}
                >
                  {fromAgent ? brand.initial : person.initials}
                </span>
                <div className='min-w-0'>
                  <p className='flex items-baseline gap-1.5'>
                    <span className='text-[14px] font-bold text-[#1d1c1d] dark:text-white'>
                      {fromAgent ? brand.name : person.name}
                    </span>
                    {fromAgent && (
                      <span className='rounded bg-[#1d1c1d]/[0.08] px-1 py-px text-[9.5px] font-bold uppercase tracking-wide text-[#616061] dark:bg-white/10 dark:text-[#ababad]'>
                        {t('App')}
                      </span>
                    )}
                    <span
                      dir='ltr'
                      className='text-[11px] tabular-nums text-[#616061] dark:text-[#ababad]'
                    >
                      {line.time}
                    </span>
                  </p>
                  <div className='mt-0.5'>{line.text}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className='bg-white px-3 pb-4 pt-1 dark:bg-[#1a1d21]'>
        <div className='rounded-xl px-3 py-2.5 text-[13.5px] text-[#868686] ring-1 ring-black/15 dark:ring-white/20'>
          {t('Message {{name}}', { name: brand.name })}
          <div className='mt-2 flex items-center gap-3 text-[#616061] dark:text-[#ababad]'>
            <PlusIcon className='h-4 w-4' />
            <AtSymbolIcon className='h-4 w-4' />
            <PaperAirplaneIcon className='ms-auto h-4 w-4 rotate-90 rtl:-rotate-90' />
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
